import { oids } from "@/configs/Settings";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import type { MessagingPatient } from "src-next/modules/crm-clinical/messaging/types";

import type { CrmPatientSummary } from "../types";

type RelatedPersonBundleEntry = {
  resource?: {
    id?: string;
    patient?: { reference?: string };
    name?: Array<{ use?: string; text?: string }>;
    telecom?: Array<{ system?: string; value?: string }>;
    identifier?: Array<{ system?: string; value?: string }>;
  };
};

function pickPreferredNameText(name?: Array<{ use?: string; text?: string }>) {
  const list = name ?? [];
  const official = list.find((n) => n.use === "official")?.text?.trim();
  if (official) return official;
  const usual = list.find((n) => n.use === "usual")?.text?.trim();
  if (usual) return usual;
  return list[0]?.text?.trim() ?? "";
}

function normalizeSpaces(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function normalizeDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function pickTutorCpfFromIdentifiers(
  identifiers: Array<{ system?: string; value?: string }> | undefined,
): string {
  const list = identifiers ?? [];
  const byCpfOid = list.find(
    (row) => String(row.system ?? "").trim() === oids.cpf,
  );
  const preferred = String(byCpfOid?.value ?? "").trim();
  if (preferred) return preferred;
  for (const row of list) {
    const v = String(row.value ?? "").trim();
    if (v) return v;
  }
  return "";
}

function toTutorGroupKey(params: {
  cpf?: string;
  fullName: string;
  phone?: string;
}) {
  const cpf = String(params.cpf ?? "").trim();
  if (cpf) return cpf;
  const nameKey = normalizeSpaces(params.fullName).toLowerCase();
  const phoneKey = normalizeDigits(String(params.phone ?? ""));
  return `name_phone_${nameKey}_${phoneKey}`;
}

/**
 * Vet mode: projeta pets (CrmPatientSummary) em tutores (MessagingPatient) via RelatedPerson.
 * Reutilizado no dialog de envio e no disparo em lote de aniversariantes.
 */
export async function resolveMessagingTutorsFromCrmPets(
  pets: CrmPatientSummary[],
): Promise<MessagingPatient[]> {
  const petIds = pets
    .map((p) => String(p.patientId ?? "").trim())
    .filter(Boolean);
  if (petIds.length === 0) return [];

  const response = await GetRelatedPerson({
    patient: petIds.join(","),
    vetRelated: true,
  });

  const entries = (response?.data?.entry ?? []) as RelatedPersonBundleEntry[];

  const grouped: Record<string, MessagingPatient> = {};

  for (const entry of entries) {
    const rp = entry.resource;
    const relatedPersonId = String(rp?.id ?? "").trim();
    const patientRef = String(rp?.patient?.reference ?? "").trim();
    if (!relatedPersonId || !patientRef) continue;

    const fullName = normalizeSpaces(pickPreferredNameText(rp?.name)) || "-";
    const phone =
      rp?.telecom?.find((t) => t.system === "phone")?.value?.trim() ?? "";
    const cpf = pickTutorCpfFromIdentifiers(rp?.identifier).trim();

    const groupKey = toTutorGroupKey({ cpf, fullName, phone });
    if (!grouped[groupKey]) {
      grouped[groupKey] = {
        id: relatedPersonId,
        fullName,
        cpf: cpf || undefined,
        phone: phone || undefined,
        isTutor: true,
        forReference: undefined,
      };
    } else {
      if (!grouped[groupKey].cpf && cpf) grouped[groupKey].cpf = cpf;
      if (!grouped[groupKey].phone && phone) grouped[groupKey].phone = phone;
    }
  }

  return Object.values(grouped);
}
